import * as React from "react";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import type { VehicleResult } from "@fm/ui";
import {
  buildFilterViews,
  buildPills,
  clearFilters,
  countActiveFilters,
  findBestFilterToRemove,
  formatPrice,
  orderOptionKeys,
  removeFilter,
  removeOption,
  SORT_OPTIONS,
  type FilterContext,
  type FilterSelection,
  type FilterView,
  type SortKey,
} from "./filters";
import { FILTER_STYLES } from "./styles";

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

// On a phone the menu chips open the one bottom sheet, as in the prototype.
const isPhone = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(max-width: 640px)").matches;

const TOGGLE_COPY: Record<string, { title: string; sub: string }> = {
  pet_friendly: { title: "Pet friendly", sub: "Pets allowed" },
  delivery: { title: "Delivery", sub: "Delivered to your campsite" },
  festivals: {
    title: "Festival friendly",
    sub: "Allowed at festivals and events",
  },
  unlimited_miles: { title: "Unlimited mileage", sub: "No per-mile charge" },
};

export interface RVSearchFiltersProps {
  /** The vehicles the search returned, before any filter (guests applied). */
  vehicles: VehicleResult[];
  /** What applies now: already resolved against `vehicles`. */
  selection: FilterSelection;
  sort: SortKey;
  ctx: FilterContext;
  /** How many vehicles are left once every filter is applied. */
  shownCount: number;
  theme?: "light" | "dark";
  onSelectionChange: (next: FilterSelection) => void;
  onSortChange: (sort: SortKey) => void;
}

type OpenState = string | "sheet" | null;

/**
 * The filter bar, the count row with its pills and the Sort menu, and on a
 * phone the "Filters" bottom sheet. Renders from `buildFilterViews`, so it
 * knows nothing about any one filter beyond how each kind looks.
 */
export function RVSearchFilters({
  vehicles,
  selection,
  sort,
  ctx,
  shownCount,
  theme = "light",
  onSelectionChange,
  onSortChange,
}: RVSearchFiltersProps) {
  const [open, setOpen] = React.useState<OpenState>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const views = React.useMemo(
    () => buildFilterViews(vehicles, selection, ctx),
    [vehicles, selection, ctx],
  );
  const pills = React.useMemo(
    () => buildPills(vehicles, selection, ctx),
    [vehicles, selection, ctx],
  );
  const activeCount = countActiveFilters(selection);
  const offeredViews = views.filter((v) => v.offered);
  const barViews = offeredViews.filter((v) => v.placement === "bar");
  const moreViews = offeredViews.filter((v) => v.placement === "more");
  const moreActive = views.filter(
    (v) => v.placement === "more" && v.active,
  ).length;
  // A set filter is never disabled, so an active one keeps the chip usable.
  const moreDisabled = moreViews.every((v) => v.disabled);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const el = rootRef.current?.querySelector(
        open === "sheet" || open === "more"
          ? ".fm-rvf-sheet"
          : `[data-anchor="${open}"]`,
      );
      if (el && !el.contains(target)) setOpen(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (vehicles.length === 0) return null;

  const setToggle = (key: string, on: boolean) =>
    onSelectionChange(
      on ? { ...selection, [key]: true } : removeFilter(selection, key, ctx),
    );

  const countText =
    activeCount > 0 ? (
      <>
        Showing <b>{shownCount}</b> of {plural(vehicles.length, "RV", "RVs")}
      </>
    ) : (
      <b>{plural(vehicles.length, "RV", "RVs")}</b>
    );

  const sectionProps = {
    vehicles,
    selection,
    ctx,
    onSelectionChange,
  };

  const renderMenuBody = (view: FilterView) =>
    view.kind === "range" ? (
      <PriceSection view={view} {...sectionProps} />
    ) : view.groups && view.groups.length > 0 ? (
      <TypeSection view={view} {...sectionProps} />
    ) : (
      <OptionSection view={view} {...sectionProps} />
    );

  const toggleRows = (list: FilterView[]) =>
    list.map((view) => {
      const copy = TOGGLE_COPY[view.key] ?? { title: view.label, sub: "" };
      return (
        <div
          className={`fm-rvf-toggle-row${view.disabled ? " is-disabled" : ""}`}
          key={view.key}
        >
          <span>
            {copy.title}
            <small>
              {copy.sub ? `${copy.sub} · ` : ""}
              {plural(view.count ?? 0, "RV", "RVs")}
            </small>
          </span>
          <button
            type="button"
            role="switch"
            className="fm-rvf-switch"
            aria-checked={view.active}
            aria-label={copy.title}
            disabled={view.disabled}
            onClick={() => setToggle(view.key, !view.active)}
          />
        </div>
      );
    });

  // Length, year, booking mode and the "rules and extras" toggles: the body of
  // the desktop dialog and the last sections of the phone sheet.
  const moreSections = () => {
    const toggles = moreViews.filter((v) => v.kind === "toggle");
    return (
      <>
        {moreViews
          .filter((v) => v.kind === "choice")
          .map((view) => (
            <div className="fm-rvf-sheet-sec" key={view.key}>
              <h4>{view.label}</h4>
              <ChoiceSection view={view} {...sectionProps} />
            </div>
          ))}
        {toggles.length > 0 && (
          <div className="fm-rvf-sheet-sec">
            <h4>Rules and extras</h4>
            {toggleRows(toggles)}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="fm-rvf" data-theme={theme} ref={rootRef}>
      <style>{FILTER_STYLES}</style>
      <div className="fm-rvf-wrap">
        <div className="fm-rvf-bar" role="toolbar" aria-label="Filters">
          {offeredViews.length > 0 && (
            <button
              type="button"
              className={`fm-rvf-chip fm-rvf-phone${activeCount ? " is-set" : ""}`}
              aria-haspopup="dialog"
              aria-expanded={open === "sheet"}
              onClick={() => setOpen(open === "sheet" ? null : "sheet")}
            >
              <SlidersHorizontal aria-hidden="true" />
              Filters{activeCount ? ` (${activeCount})` : ""}
            </button>
          )}
          {barViews.map((view) =>
            view.kind === "toggle" ? (
              <button
                key={view.key}
                type="button"
                className="fm-rvf-chip"
                aria-pressed={view.active}
                disabled={view.disabled}
                onClick={() => setToggle(view.key, !view.active)}
              >
                {view.label}
              </button>
            ) : (
              <div
                key={view.key}
                className="fm-rvf-anchor fm-rvf-desk"
                data-anchor={view.key}
              >
                <button
                  type="button"
                  className={`fm-rvf-chip${view.active ? " is-set" : ""}`}
                  aria-haspopup="dialog"
                  aria-expanded={open === view.key}
                  disabled={view.disabled}
                  onClick={() =>
                    setOpen(
                      isPhone() ? "sheet" : open === view.key ? null : view.key,
                    )
                  }
                >
                  {view.chipLabel}
                  <ChevronDown aria-hidden="true" />
                </button>
                {open === view.key && (
                  <Popover
                    title={
                      view.kind === "range" ? "Price per night" : view.label
                    }
                    count={shownCount}
                    onClear={() =>
                      onSelectionChange(removeFilter(selection, view.key, ctx))
                    }
                  >
                    {renderMenuBody(view)}
                  </Popover>
                )}
              </div>
            ),
          )}
          {moreViews.length > 0 && (
            <button
              type="button"
              className={`fm-rvf-chip fm-rvf-desk${moreActive ? " is-set" : ""}`}
              aria-haspopup="dialog"
              aria-expanded={open === "more"}
              disabled={moreDisabled}
              onClick={() => setOpen(open === "more" ? null : "more")}
            >
              <SlidersHorizontal aria-hidden="true" />
              More filters{moreActive ? ` (${moreActive})` : ""}
            </button>
          )}
        </div>
      </div>

      <div className="fm-rvf-meta">
        <span className="fm-rvf-count" aria-live="polite">
          {countText}
        </span>
        {pills.map((pill) => (
          <button
            key={pill.id}
            type="button"
            className="fm-rvf-pill"
            aria-label={`Remove ${pill.label}`}
            onClick={() =>
              onSelectionChange(
                pill.id.includes(":")
                  ? removeOption(
                      selection,
                      pill.filterKey,
                      pill.id.slice(pill.filterKey.length + 1),
                      ctx,
                    )
                  : removeFilter(selection, pill.filterKey, ctx),
              )
            }
          >
            {pill.label}
            <span aria-hidden="true">×</span>
          </button>
        ))}
        {activeCount > 0 && (
          <button
            type="button"
            className="fm-rvf-link"
            onClick={() => onSelectionChange(clearFilters(selection, ctx))}
          >
            Clear all
          </button>
        )}
        <label className="fm-rvf-sort">
          Sort
          <select
            value={sort}
            onChange={(event) => onSortChange(event.target.value as SortKey)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {(open === "sheet" || open === "more") && (
        <div
          className={`fm-rvf-layer${open === "more" ? " modal" : ""}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(null);
          }}
        >
          <div
            className="fm-rvf-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={open === "more" ? "More filters" : "Filters"}
          >
            {open === "sheet" && <div className="fm-rvf-grabber" />}
            <div className="fm-rvf-sheet-head">
              <strong>{open === "more" ? "More filters" : "Filters"}</strong>
              <button
                type="button"
                className="fm-rvf-icon-btn"
                aria-label="Close"
                onClick={() => setOpen(null)}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="fm-rvf-sheet-body">
              {open === "more" ? (
                moreSections()
              ) : (
                <>
                  {barViews
                    .filter((view) => view.kind !== "toggle")
                    .map((view) => (
                      <div className="fm-rvf-sheet-sec" key={view.key}>
                        <h4>
                          {view.kind === "range"
                            ? "Price per night"
                            : view.label}
                        </h4>
                        {renderMenuBody(view)}
                      </div>
                    ))}
                  {barViews.some((view) => view.kind === "toggle") && (
                    <div className="fm-rvf-sheet-sec">
                      <h4>Rules and delivery</h4>
                      {toggleRows(barViews.filter((v) => v.kind === "toggle"))}
                    </div>
                  )}
                  {moreSections()}
                </>
              )}
            </div>
            <div className="fm-rvf-sheet-foot">
              <button
                type="button"
                className="fm-rvf-link"
                onClick={() => onSelectionChange(clearFilters(selection, ctx))}
              >
                Clear all
              </button>
              <button
                type="button"
                className="fm-rvf-btn fm-rvf-btn-primary"
                onClick={() => setOpen(null)}
              >
                Show {plural(shownCount, "RV", "RVs")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Popover({
  title,
  count,
  onClear,
  children,
}: {
  title: string;
  count: number;
  onClear: () => void;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [align, setAlign] = React.useState<"left" | "right">("left");

  // Flip to the anchor's right edge when the popover would leave the window.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.getBoundingClientRect().right > window.innerWidth) {
      setAlign("right");
    }
  }, []);

  return (
    <div
      ref={ref}
      className="fm-rvf-pop"
      role="dialog"
      aria-label={title}
      data-align={align}
    >
      <h4>{title}</h4>
      {children}
      <div className="fm-rvf-foot">
        <span>{plural(count, "RV", "RVs")}</span>
        <button type="button" className="fm-rvf-link" onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
  );
}

interface SectionProps {
  view: FilterView;
  vehicles: VehicleResult[];
  selection: FilterSelection;
  ctx: FilterContext;
  onSelectionChange: (next: FilterSelection) => void;
}

/** "All RV types" plus Drivable / Towable / Other with their types. */
function TypeSection({
  view,
  vehicles,
  selection,
  ctx,
  onSelectionChange,
}: SectionProps) {
  const picked = (selection[view.key] as string[] | undefined) ?? [];
  const options = view.options ?? [];

  const commit = (keys: string[]) =>
    onSelectionChange(
      keys.length === 0
        ? removeFilter(selection, view.key, ctx)
        : {
            ...selection,
            [view.key]: orderOptionKeys(view.key, vehicles, keys),
          },
    );

  return (
    <div>
      <div className="fm-rvf-opt all">
        <input
          type="radio"
          id={`rvf-${view.key}-all`}
          name={`rvf-${view.key}-all`}
          checked={picked.length === 0}
          onChange={() => commit([])}
        />
        <label htmlFor={`rvf-${view.key}-all`}>
          <span>All RV types</span>
          <span className="c">{view.allCount}</span>
        </label>
      </div>
      {(view.groups ?? []).map((group) => {
        const allPicked =
          group.optionKeys.length > 0 &&
          group.optionKeys.every((k) => picked.includes(k));
        return (
          <React.Fragment key={group.group}>
            <div className="fm-rvf-opt group">
              <input
                type="checkbox"
                id={`rvf-${view.key}-group-${group.group}`}
                checked={allPicked}
                disabled={group.disabled}
                onChange={() =>
                  commit(
                    allPicked
                      ? picked.filter((k) => !group.optionKeys.includes(k))
                      : [...new Set([...picked, ...group.optionKeys])],
                  )
                }
              />
              <label htmlFor={`rvf-${view.key}-group-${group.group}`}>
                <span>{group.label}</span>
                <span className="c">{group.count}</span>
              </label>
            </div>
            {options
              .filter((o) => o.group === group.group)
              .map((option) => (
                <div className="fm-rvf-opt sub" key={option.key}>
                  <input
                    type="checkbox"
                    id={`rvf-${view.key}-${option.key}`}
                    checked={option.selected}
                    disabled={option.disabled}
                    onChange={() =>
                      commit(
                        option.selected
                          ? picked.filter((k) => k !== option.key)
                          : [...picked, option.key],
                      )
                    }
                  />
                  <label htmlFor={`rvf-${view.key}-${option.key}`}>
                    <span>
                      {option.name}
                      {option.description && (
                        <small>{option.description}</small>
                      )}
                    </span>
                    <span className="c">{option.count}</span>
                  </label>
                </div>
              ))}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** A flat list of options (amenities), each with the vehicles it leaves. */
function OptionSection({
  view,
  vehicles,
  selection,
  ctx,
  onSelectionChange,
}: SectionProps) {
  const picked = (selection[view.key] as string[] | undefined) ?? [];
  const commit = (keys: string[]) =>
    onSelectionChange(
      keys.length === 0
        ? removeFilter(selection, view.key, ctx)
        : {
            ...selection,
            [view.key]: orderOptionKeys(view.key, vehicles, keys),
          },
    );

  return (
    <div>
      {(view.options ?? []).map((option) => (
        <div className="fm-rvf-opt" key={option.key}>
          <input
            type="checkbox"
            id={`rvf-${view.key}-${option.key}`}
            checked={option.selected}
            disabled={option.disabled}
            onChange={() =>
              commit(
                option.selected
                  ? picked.filter((k) => k !== option.key)
                  : [...picked, option.key],
              )
            }
          />
          <label htmlFor={`rvf-${view.key}-${option.key}`}>
            <span>{option.name}</span>
            <span className="c">{option.count}</span>
          </label>
        </div>
      ))}
    </div>
  );
}

/** Single-choice steps ("Up to 30 ft"): any, or one step with its count. */
function ChoiceSection({
  view,
  selection,
  ctx,
  onSelectionChange,
}: SectionProps) {
  return (
    <div>
      <div className="fm-rvf-seg">
        <button
          type="button"
          aria-pressed={!view.active}
          onClick={() =>
            onSelectionChange(removeFilter(selection, view.key, ctx))
          }
        >
          {view.anyLabel}
        </button>
        {(view.choices ?? []).map((choice) => (
          <button
            key={choice.value}
            type="button"
            aria-pressed={choice.selected}
            disabled={choice.disabled}
            onClick={() =>
              onSelectionChange({ ...selection, [view.key]: choice.value })
            }
          >
            {choice.label} · {choice.count}
          </button>
        ))}
      </div>
      {view.key === "length_max" && (
        <p className="fm-rvf-hint">Check your campsite&apos;s length limit.</p>
      )}
    </div>
  );
}

/** A range over the prices present in the results, with a histogram. */
function PriceSection({
  view,
  selection,
  ctx,
  onSelectionChange,
}: SectionProps) {
  const points = view.points ?? [];
  const prices = view.prices ?? [];
  if (points.length === 0)
    return <p className="fm-rvf-hint">No RVs to compare.</p>;

  const last = points.length - 1;
  const snap = (value: number) =>
    points.reduce(
      (best, p, i) =>
        Math.abs(p - value) < Math.abs(points[best]! - value) ? i : best,
      0,
    );
  const current = selection[view.key] as [number, number] | undefined;
  const loIdx = current ? snap(current[0]) : 0;
  const hiIdx = current ? snap(current[1]) : last;
  const lo = points[loIdx]!;
  const hi = points[hiIdx]!;

  const commit = (nextLo: number, nextHi: number) => {
    if (nextLo === 0 && nextHi === last) {
      onSelectionChange(removeFilter(selection, view.key, ctx));
    } else {
      onSelectionChange({
        ...selection,
        [view.key]: [points[nextLo]!, points[nextHi]!],
      });
    }
  };

  const bins = 16;
  const span = Math.max(1, points[last]! - points[0]!);
  const step = span / bins;
  const counts = new Array<number>(bins).fill(0);
  for (const price of prices) {
    counts[Math.min(bins - 1, Math.floor((price - points[0]!) / step))]!++;
  }
  const max = Math.max(1, ...counts);
  const inRange = prices.filter((p) => p >= lo && p <= hi).length;
  const left = ((lo - points[0]!) / span) * 100;
  const right = ((hi - points[0]!) / span) * 100;

  return (
    <div>
      <div className="fm-rvf-vals">
        <span>{formatPrice(lo, ctx.currency)}</span>
        <span>{formatPrice(hi, ctx.currency)}</span>
      </div>
      <div className="fm-rvf-hist" aria-hidden="true">
        {counts.map((n, i) => {
          const mid = points[0]! + step * (i + 0.5);
          return (
            <i
              key={i}
              className={n === 0 ? "empty" : mid >= lo && mid <= hi ? "on" : ""}
              style={{ height: `${Math.round((n / max) * 100)}%` }}
            />
          );
        })}
      </div>
      <div className="fm-rvf-range">
        <span className="fm-rvf-track" />
        <span
          className="fm-rvf-fill"
          style={{ left: `${left}%`, right: `${100 - right}%` }}
        />
        <input
          type="range"
          aria-label="Minimum price per night"
          min={0}
          max={last}
          step={1}
          value={loIdx}
          disabled={last < 1}
          onChange={(e) =>
            commit(Math.min(Number(e.target.value), hiIdx), hiIdx)
          }
        />
        <input
          type="range"
          aria-label="Maximum price per night"
          min={0}
          max={last}
          step={1}
          value={hiIdx}
          disabled={last < 1}
          onChange={(e) =>
            commit(loIdx, Math.max(Number(e.target.value), loIdx))
          }
        />
      </div>
      <p className="fm-rvf-hint">
        Per night. The handles stop only at prices of RVs in these results.{" "}
        {plural(inRange, "RV", "RVs")} in this range.
      </p>
    </div>
  );
}

/**
 * What "No vehicles found" offers when filters, not the search, emptied the
 * list: the filter whose removal brings back the most RVs, and Clear filters.
 */
export function FilterEmptyActions({
  vehicles,
  selection,
  ctx,
  onSelectionChange,
}: {
  vehicles: VehicleResult[];
  selection: FilterSelection;
  ctx: FilterContext;
  onSelectionChange: (next: FilterSelection) => void;
}) {
  const best = findBestFilterToRemove(vehicles, selection, ctx);
  return (
    <div className="fm-rvf fm-rvf-empty-actions">
      <style>{FILTER_STYLES}</style>
      {best && (
        <button
          type="button"
          className="fm-rvf-btn"
          onClick={() =>
            onSelectionChange(removeFilter(selection, best.key, ctx))
          }
        >
          Remove “{best.label}”: {plural(best.count, "RV", "RVs")}
        </button>
      )}
      <button
        type="button"
        className="fm-rvf-btn fm-rvf-btn-primary"
        onClick={() => onSelectionChange(clearFilters(selection, ctx))}
      >
        Clear filters
      </button>
    </div>
  );
}
